<template>
  <main class="bg-about-background bg-top pb-20 bg-[length:130rem] h-full bg-no-repeat bg-white pt-20">
    <section class="max-w-screen-lg px-4 mx-auto">
      <div class="mb-4">
        <router-link
          class="text-sm font-medium text-gray-900 hover:text-primary"
          to="/dashboard/events"
        >
          <ArrowLeftIcon class="inline w-4 h-4 mr-2" />
          Back to Events
        </router-link>
      </div>
      <div
        v-if="loading"
        class="text-sm text-gray-500"
      >
        Loading event...
      </div>
      <div
        v-else-if="error"
        class="text-sm text-red-600"
      >
        {{ error }}
      </div>
      <div
        v-else
        class="space-y-10"
      >
        <div
          v-if="bannerImageSrc"
          class="overflow-hidden bg-white border border-gray-200 rounded-2xl"
        >
          <img
            :alt="event?.title || 'Event banner'"
            :src="bannerImageSrc"
            class="h-[600px] w-full object-cover"
          >
        </div>
        <div class="rounded-2xl border border-gray-200 bg-[#f6f1ec] p-8 shadow-sm">
          <div class="flex flex-col gap-6 lg:flex-row lg:items-start lg:justify-between">
            <div class="max-w-3xl">
              <p class="text-xs uppercase tracking-[0.2em] text-gray-500">
                Event Details
              </p>
              <h1 class="mt-3 text-2xl font-semibold text-gray-900 md:text-3xl">
                {{ event?.title }}
              </h1>
              <div class="flex flex-wrap items-center gap-3 mt-4 text-sm text-gray-700">
                <span class="px-3 py-1 text-xs font-medium text-gray-600 bg-white rounded-full">
                  {{ formatDate(event?.date) }}
                </span>
                <span
                  v-if="event?.startTime || event?.endTime"
                  class="px-3 py-1 text-xs font-medium text-gray-600 bg-white rounded-full"
                >
                  {{ formatTimeRange(event?.startTime, event?.endTime) }}
                </span>
                <span
                  v-if="event?.location"
                  class="px-3 py-1 text-xs font-medium text-gray-600 bg-white rounded-full"
                >
                  {{ event.location }}
                </span>
                <span
                  v-if="event?.type"
                  class="px-3 py-1 text-xs font-medium text-gray-600 bg-white rounded-full"
                >
                  {{ event.type }}
                </span>
              </div>
            </div>
            <div class="flex flex-col items-start gap-3 lg:items-end">
              <div class="flex flex-col w-full gap-3 sm:w-56">
                <router-link
                  class="inline-flex items-center justify-center w-full px-5 py-2 text-sm font-medium text-gray-900 bg-white border border-gray-300 rounded-full hover:bg-gray-100"
                  to="/dashboard/events"
                >
                  <ArrowLeftIcon class="w-4 h-4 mr-2" />
                  Back to Events
                </router-link>
                <button
                  class="inline-flex items-center justify-center w-full px-5 py-2 text-sm font-medium text-gray-900 bg-white border border-gray-300 rounded-full hover:bg-gray-100"
                  type="button"
                  @click="shareEvent"
                >
                  <ShareIcon class="w-4 h-4 mr-2" />
                  Share
                </button>
                <router-link
                  v-if="user?.role === 'admin'"
                  class="inline-flex items-center justify-center w-full px-5 py-2 text-sm font-medium text-gray-900 bg-white border border-gray-300 rounded-full hover:bg-gray-100"
                  :to="`/administrator/events/${id}`"
                >
                  <PencilSquareIcon class="w-4 h-4 mr-2" />
                  Edit Event
                </router-link>
              </div>
              <router-link
                v-if="!user"
                class="inline-flex w-full items-center justify-center rounded-full bg-primary px-5 py-2 text-sm font-semibold text-black hover:bg-[#c9ad99] sm:w-56"
                to="/login"
              >
                Log in to reserve
              </router-link>
              <a
                v-else-if="event?.ticketUrl"
                class="inline-flex w-full items-center justify-center rounded-full bg-primary px-5 py-2 text-sm font-semibold text-black hover:bg-[#c9ad99] sm:w-56"
                :href="event.ticketUrl"
                rel="noreferrer"
                target="_blank"
              >
                <TicketIcon class="w-4 h-4 mr-2" />
                Reserve your spot
              </a>
            </div>
          </div>
        </div>

        <div class="grid gap-8 lg:grid-cols-[2fr,1fr]">
          <div class="space-y-8">
            <div class="p-6 bg-white border border-gray-200 shadow-sm rounded-2xl">
              <h2 class="text-lg font-semibold text-gray-900">
                Overview
              </h2>
              <p class="mt-3 text-sm leading-6 text-gray-700">
                {{ event?.description || 'Join this exclusive member event to connect with peers, share insights, and expand your investor network.' }}
              </p>
            </div>
            <div class="p-6 bg-white border border-gray-200 shadow-sm rounded-2xl">
              <h2 class="text-lg font-semibold text-gray-900">
                Who Should Join
              </h2>
              <div class="grid gap-3 mt-4 sm:grid-cols-2">
                <div
                  v-for="item in whoShouldJoinItems"
                  :key="item"
                  class="px-4 py-2 text-xs font-medium text-gray-700 border border-gray-200 rounded-full"
                >
                  {{ item }}
                </div>
              </div>
            </div>
          </div>

          <aside class="space-y-6">
            <div class="p-6 bg-white border border-gray-200 shadow-sm rounded-2xl">
              <h2 class="text-lg font-semibold text-gray-900">
                Event Details
              </h2>
              <div class="grid gap-4 mt-4 text-sm text-gray-700">
                <div class="flex items-center justify-between">
                  <span class="text-gray-500">Date</span>
                  <span class="font-medium text-gray-900">
                    {{ formatDate(event?.date) }}
                  </span>
                </div>
                <div class="flex items-center justify-between">
                  <span class="text-gray-500">Time</span>
                  <span class="font-medium text-gray-900">
                    {{ formatTimeRange(event?.startTime, event?.endTime) || 'TBA' }}
                  </span>
                </div>
                <div class="flex items-center justify-between">
                  <span class="text-gray-500">Location</span>
                  <span class="font-medium text-gray-900">
                    {{ event?.location || 'TBA' }}
                  </span>
                </div>
                <div class="flex items-center justify-between">
                  <span class="text-gray-500">Type</span>
                  <span class="font-medium text-gray-900">
                    {{ event?.type || 'Member Event' }}
                  </span>
                </div>
                <div class="flex items-center justify-between">
                  <span class="text-gray-500">Price</span>
                  <span class="font-medium text-gray-900">
                    <span v-if="event?.hidePrice">Paid event</span>
                    <span v-else-if="typeof event?.price === 'number'">£{{ formatPrice(event.price) }}</span>
                    <span v-else>Free</span>
                  </span>
                </div>
              </div>
              <router-link
                v-if="!user"
                class="mt-5 inline-flex w-full items-center justify-center rounded-full bg-primary px-5 py-2 text-sm font-semibold text-black hover:bg-[#c9ad99]"
                to="/login"
              >
                Log in to reserve
              </router-link>
              <a
                v-else-if="event?.ticketUrl"
                class="mt-5 inline-flex w-full items-center justify-center rounded-full bg-primary px-5 py-2 text-sm font-semibold text-black hover:bg-[#c9ad99]"
                :href="event.ticketUrl"
                rel="noreferrer"
                target="_blank"
              >
                Get ticket
              </a>
            </div>
          </aside>
        </div>
      </div>
    </section>
  </main>
</template>

<script lang="ts">
import {defineComponent, inject, onMounted, ref, watchEffect} from 'vue';
import axios from 'axios';
import {Event} from '@/types';
import {settingsKey} from "@/composables/useSettings";
import {useHead} from "@vueuse/head";
import {userKey} from "@/composables/useUser";
import {TicketIcon, ArrowLeftIcon, ShareIcon, PencilSquareIcon} from "@heroicons/vue/24/outline";

export default defineComponent({
  name: 'EventDetail',
  components: {TicketIcon, ArrowLeftIcon, ShareIcon, PencilSquareIcon},
  props: {
    id: {
      type: String,
      required: true,
    },
  },
  setup(props) {
    const {settings} = inject(settingsKey)!;
    const {user} = inject(userKey)!;
    const event = ref<Event | null>(null);
    const bannerImageSrc = ref('');
    const loading = ref(false);
    const error = ref('');
    const whoShouldJoinItems = ref<string[]>([]);

    const resolveBannerImageSrc = async (image?: string): Promise<string> => {
      const raw = (image || '').trim();
      if (!raw) return '';
      if (raw.startsWith('http://') || raw.startsWith('https://')) {
        return raw;
      }

      try {
        const response = await axios.get<{ url: string }>(
          `/video/get-signed-url?fileName=${encodeURIComponent(raw)}`
        );
        return response.data.url || '';
      } catch (err) {
        console.error('Failed to load event banner URL:', err);
        return '';
      }
    };

    const fetchEvent = async (): Promise<void> => {
      loading.value = true;
      try {
        const response = await axios.get<Event>(`/events/${props.id}`);
        event.value = response.data;
        bannerImageSrc.value = await resolveBannerImageSrc(event.value?.bannerImageUrl);
        whoShouldJoinItems.value = event.value?.whoShouldJoin?.length
          ? event.value.whoShouldJoin
          : [
            'Founders scaling their next round',
            'Angel investors & syndicate leads',
            'Venture partners & LPs',
            'Operators exploring deal flow',
          ];
      } catch (err) {
        console.error('Failed to load event:', err);
        error.value = 'Event not found.';
      } finally {
        loading.value = false;
      }
    };

    const formatDate = (value?: string): string => {
      if (!value) return 'Unknown date';
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) return 'Unknown date';
      return new Intl.DateTimeFormat('en-GB', {
        day: '2-digit',
        month: 'long',
        year: 'numeric',
      }).format(date);
    };

    const formatTimeRange = (start?: string, end?: string): string => {
      if (start && end) return `${start} to ${end}`;
      if (start) return `From ${start}`;
      if (end) return `Until ${end}`;
      return '';
    };

    const formatPrice = (value: number): string => {
      return value.toFixed(2);
    };

    onMounted(fetchEvent);

    watchEffect(() => {
      if (settings.value) {
        const baseUrl = window.location.origin;
        const pageUrl = `${baseUrl}/dashboard/events/${props.id}`;
        const title = event.value?.title || settings.value?.metaTitle || 'Event';
        const description = event.value?.description || settings.value?.metaDescription || '';
        const fallbackImageUrl = settings.value?.logoUrl || '';
        const imageUrl = bannerImageSrc.value || fallbackImageUrl;

        useHead({
          title,
          link: [
            {rel: 'canonical', href: pageUrl},
          ],
          meta: [
            {name: 'description', content: description},
            {property: 'og:type', content: 'website'},
            {property: 'og:title', content: title},
            {property: 'og:description', content: description},
            {property: 'og:url', content: pageUrl},
            ...(imageUrl ? [{property: 'og:image', content: imageUrl}] : []),
            {name: 'twitter:card', content: 'summary_large_image'},
            {name: 'twitter:title', content: title},
            {name: 'twitter:description', content: description},
            ...(imageUrl ? [{name: 'twitter:image', content: imageUrl}] : []),
          ],
        });
      }
    });

    const shareEvent = async (): Promise<void> => {
      if (!event.value) return;
      const shareData = {
        title: event.value.title,
        text: event.value.description || 'Join this member event.',
        url: window.location.href,
      };

      try {
        if (navigator.share) {
          await navigator.share(shareData);
        } else if (navigator.clipboard) {
          await navigator.clipboard.writeText(shareData.url);
        }
      } catch (err) {
        console.error('Share failed:', err);
      }
    };

    return {
      event,
      bannerImageSrc,
      loading,
      error,
      user,
      whoShouldJoinItems,
      formatDate,
      formatTimeRange,
      formatPrice,
      shareEvent,
    };
  },
});
</script>
